import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { Box, Paper } from '@mui/material';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Calendar, Save, User } from 'lucide-react';
import { format } from 'date-fns';
import {  SimpleRecipe as Recipe, DraftData } from '@/types/meal-plan';
import RecipeDetailsModal from '@/components/meal-planning/RecipeDetailsModal';
import ClientGoalsCard from '@/components/meal-planning/ai/ClientGoalsCard';
import MealSelectionValidationModal from '@/components/meal-planning/MealSelectionValidationModal';
import DayNutritionAnalysis from '@/components/meal-planning/DayNutritionAnalysis';
import { showErrorToast, showSuccessToast, showWarningToast } from '@/utils/toast-utils';
import { getMealPlanDraftById, getMealPlanForDisplay, selectDraftRecipe, finalizeDraft, deselectDraftRecipe, changeMealPage, getClientById, updateMealPlanDayName } from '@/lib/apiService';
import FeedbackButton from '@/components/feedback/FeedbackButton';
import { FeedbackContext } from '@/components/feedback/FeedbackPanel';
import { getNormalizedCreationMethod, getPlanningFeedbackContext } from '@/utils/feedback';
import { resolveClientGoals } from '@/utils/client-goals';
import { buildGoalDisplayItems } from '@/utils/nutritionGoals';
import { buildMealSlotTargetsSummary } from '@/utils/meal-slot-targets';
import MealPlanDayHeader from '@/components/meal-planning/display/MealPlanDayHeader';
import { DayNameEditPopover } from '@/components/meal-planning/DayNameEditPopover';
import MealPlanMealSection from '@/components/meal-planning/display/MealPlanMealSection';
import ClientDetailsBadge from '@/components/meal-planning/ClientDetailsBadge';
import { getMealOrderByMealKey } from '@/utils/meal-type-utils';

type DraftSelectionUpdateParams = {
  draftData: DraftData | null;
  dayNumber: number;
  mealType: string;
  recipeId: string;
  shouldSelect: boolean;
  dayWiseNutrition?: DraftData['dayWiseNutrition'];
};

const updateDraftSelectionState = ({
  draftData,
  dayNumber,
  mealType,
  recipeId,
  shouldSelect,
  dayWiseNutrition,
}: DraftSelectionUpdateParams): DraftData | null => {
  if (!draftData) {
    return draftData;
  }

  const dayIndex = draftData.suggestions.findIndex((day) => day.day === dayNumber);
  if (dayIndex === -1) {
    return draftData;
  }

  const targetDay = draftData.suggestions[dayIndex];
  const targetMeal = targetDay.meals?.[mealType];
  if (!targetMeal?.recipes) {
    return draftData;
  }

  const updatedMeal = {
    ...targetMeal,
    recipes: targetMeal.recipes.map((recipe) => ({
      ...recipe,
      isSelected: recipe.id === recipeId ? shouldSelect : false,
    })),
  };

  const updatedSuggestions = [...draftData.suggestions];
  updatedSuggestions[dayIndex] = {
    ...targetDay,
    meals: {
      ...targetDay.meals,
      [mealType]: updatedMeal,
    },
  };

  const nextData: DraftData = {
    ...draftData,
    suggestions: updatedSuggestions,
  };

  if (dayWiseNutrition) {
    nextData.dayWiseNutrition = dayWiseNutrition;
  }

  return nextData;
};

interface AutomatedMealDraftContentProps {
  clientId?: string;
  draftId?: string | null;
  backTarget?: string;
  initialDraftData?: DraftData | null;
  hideHeader?: boolean;
}

export const AutomatedMealDraftContent: React.FC<AutomatedMealDraftContentProps> = ({
  clientId: clientIdProp,
  draftId: draftIdProp,
  backTarget: backTargetProp,
  initialDraftData,
  hideHeader = false,
}) => {
  const { clientId: routeClientId, draftId: routeDraftId } = useParams<{ clientId: string; draftId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const locationState = location.state as { from?: string; planName?: string } | null;
  const clientId = clientIdProp ?? routeClientId;
  const draftId = draftIdProp ?? routeDraftId ?? searchParams.get('draftId');
  const fallbackBackPath = backTargetProp ?? (clientId ? `/clients/${clientId}/details-v2/meal-plan` : '/meal-planning');
  const backTarget = locationState?.from || fallbackBackPath;
  const [expandedMeals, setExpandedMeals] = useState<Set<string>>(new Set());
  const [selectedRecipeId, setSelectedRecipeId] = useState<string | null>(null);
  const [selectedRecipeName, setSelectedRecipeName] = useState<string>('');
  const [selectedRecipeContext, setSelectedRecipeContext] = useState<{ day: number; mealName: string; source: string; mealOrder?: number } | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Meal selection state
  const [mealSelections, setMealSelections] = useState<Record<string, string>>({});
  const [validationModalOpen, setValidationModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingSelections, setLoadingSelections] = useState<Set<string>>(new Set());
  
  // Draft data state
  const [draftData, setDraftData] = useState<DraftData | null>(initialDraftData ?? null);
  const [activeDayTab, setActiveDayTab] = useState<string>(() => {
    const initialDay = initialDraftData?.suggestions?.[0]?.day;
    return initialDay ? `day-${initialDay}` : 'day-1';
  });
  const [loadingDraft, setLoadingDraft] = useState(!initialDraftData);
  const [loadingPagination, setLoadingPagination] = useState<Set<string>>(new Set());
  const [clientDetails, setClientDetails] = useState<any>(null);
  const [editingDayNameFor, setEditingDayNameFor] = useState<number | null>(null);
  const [savingDayName, setSavingDayName] = useState(false);
  const planFeedbackContext = useMemo<FeedbackContext>(() => {
    const creationMethod = getNormalizedCreationMethod(draftData);
    return getPlanningFeedbackContext(creationMethod, {
      manual: FeedbackContext.MANUAL_MEAL_PLAN_GENERATION,
      ai: FeedbackContext.AI_MEAL_PLAN_GENERATION,
      auto: FeedbackContext.AUTO_MEAL_PLAN_GENERATION,
      fallback: FeedbackContext.OVERALL_APPLICATION,
    });
  }, [draftData]);

  useEffect(() => {
    if (initialDraftData) {
      setDraftData(initialDraftData);
      setLoadingDraft(false);
    }
  }, [initialDraftData]);

  // Use draft data directly without format conversion
  const data = draftData;

  useEffect(() => {
    const dayValues = data?.suggestions?.map((day) => `day-${day.day}`) ?? [];
    if (dayValues.length === 0) return;
    setActiveDayTab((previous) => (dayValues.includes(previous) ? previous : dayValues[0]));
  }, [data?.suggestions]);


  const fetchDraftData = async () => {
    if (!draftId) return;
    setLoadingDraft(true);
    try {
      const response = await getMealPlanDraftById(draftId);
      if (response.success && response.data) {
        setDraftData(response.data as DraftData);
      } else {
        throw new Error(response.message || 'Failed to load draft details');
      }
    } catch (error) {
      console.error('Error fetching draft:', error);
      showErrorToast(error, "Draft Load Failed");
    } finally {
      setLoadingDraft(false);
    }
  };

  const refetchDraftInBackground = async () => {
    if (!draftId) return;
    try {
      const response = await getMealPlanDraftById(draftId);
      if (response.success && response.data) {
        setDraftData(response.data as DraftData);
      }
    } catch {
      // Silent fail when refetching on tab switch
    }
  };

  // Fetch client details
  const fetchClientDetails = async () => {
    if (!clientId) return;
    
    try {
      const response = await getClientById(clientId);
      if (response?.client) {
        setClientDetails(response.client);
      }
    } catch (error) {
    
      // Silently fail - client details are optional
    }
  };

  // Fetch draft data when draftId is present
  useEffect(() => {
    if (draftId) {
      fetchDraftData();
    }
  }, [draftId, clientId]);

  // Fetch client details when clientId is present
  useEffect(() => {
    if (clientId) {
      fetchClientDetails();
    }
  }, [clientId]);

  // Show loading state when no draft data and draftId is present
  if (draftId && loadingDraft) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading draft meal suggestions...</p>
        </div>
      </div>
    );
  }

  // Show error state when draftId is present but no data
  if (draftId && !draftData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load Draft</h3>
          <p className="text-gray-600 mb-4">Unable to load draft meal suggestions. Please try again.</p>
          <Button onClick={fetchDraftData} disabled={loadingDraft}>
            {loadingDraft ? 'Retrying...' : 'Retry'}
          </Button>
        </div>
      </div>
    );
  }

  // Show empty state when no draftId
  if (!draftId) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="text-gray-400 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Draft Selected</h3>
          <p className="text-gray-600 mb-4">Please select a draft to view meal suggestions.</p>
          <Button onClick={() => navigate(backTarget)}>
            Go to Meal Plans
          </Button>
        </div>
      </div>
    );
  }

  // Show empty state when no data available
  if (!data || !data.suggestions) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="text-gray-400 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Meal Data Available</h3>
          <p className="text-gray-600 mb-4">No meal suggestions found in this draft.</p>
          <Button onClick={fetchDraftData} disabled={loadingDraft}>
            {loadingDraft ? 'Refreshing...' : 'Refresh Draft'}
          </Button>
        </div>
      </div>
    );
  }


  const toggleMealExpansion = (mealKey: string) => {
    const newExpanded = new Set(expandedMeals);
    if (newExpanded.has(mealKey)) {
      newExpanded.delete(mealKey);
    } else {
      newExpanded.add(mealKey);
    }
    setExpandedMeals(newExpanded);
  };

  const handleViewDetails = (recipeId: string, recipeName: string, day: number, mealName: string, source: string) => {
    setSelectedRecipeId(recipeId);
    setSelectedRecipeName(recipeName);
    const dayMeals = data.suggestions?.find((d) => d.day === day)?.meals as
      | Record<string, { mealOrder?: number }>
      | undefined;
    const mealOrder = getMealOrderByMealKey(dayMeals, mealName);
    setSelectedRecipeContext({ day, mealName, source, mealOrder });
    setModalOpen(true);
  };

  const handleModalClose = () => {
   
    // Refresh the draft to get updated nutrition values
    fetchDraftData();
  };

  // Pagination handler - calls API to fetch new page of recipes
  const handlePageChange = async (dayNumber: number, mealType: string, newPage: number) => {
    if (!draftId || !draftData) return;
    const dayMeals = draftData.suggestions?.find((d) => d.day === dayNumber)?.meals as
      | Record<string, { mealOrder?: number }>
      | undefined;
    const mealOrder = getMealOrderByMealKey(dayMeals, mealType);
    if (!mealOrder) {
      showWarningToast('Meal order missing', 'Unable to change recipe page because meal order is missing.');
      return;
    }
    const loadingKey = `${dayNumber}-${mealType}`;
    setLoadingPagination(prev => new Set(prev).add(loadingKey));
    try {
      const response = await changeMealPage(
        draftId,
        dayNumber,
        mealOrder,
        'specific',
        newPage
      );

      if (response.success && response.data) {
        // Update only the specific meal's recipes and pagination in the draft data
        setDraftData(prevData => {
          if (!prevData) return prevData;
          
          const updatedSuggestions = prevData.suggestions.map(daySuggestion => {
            if (daySuggestion.day === dayNumber) {
              return {
                ...daySuggestion,
                meals: {
                  ...daySuggestion.meals,
                  [mealType]: {
                    ...daySuggestion.meals[mealType],
                    recipes: response.data.displayedRecipes || [],
                    pagination: {
                      currentPage: response.data.currentPage,
                      totalPages: response.data.totalPages,
                      recipesPerPage: response.data.recipesCount || 6
                    }
                  }
                }
              };
            }
            return daySuggestion;
          });

          return {
            ...prevData,
            suggestions: updatedSuggestions
          };
        });

       
      }
    } catch (error) {
      showErrorToast(error, 'Error');
    } finally {
      // Clear loading state
      setLoadingPagination(prev => {
        const newSet = new Set(prev);
        newSet.delete(loadingKey);
        return newSet;
      });
    }
  };


  // Meal selection handlers
  const handleMealSelection = async (dayNumber: number, mealType: string, recipeId: string) => {
    const selectionKey = `${dayNumber}-${mealType}`;
    const loadingKey = `${dayNumber}-${mealType}-${recipeId}`;

    const isCurrentlySelected = data.suggestions
      ?.find((day) => day.day === dayNumber)
      ?.meals[mealType]?.recipes
      ?.some((recipe) => recipe.id === recipeId && recipe.isSelected);

    if (loadingSelections.has(loadingKey)) {
      return;
    }

    const isMealInFlight = Array.from(loadingSelections).some(
      (key) => key.startsWith(`${dayNumber}-${mealType}-`) && key !== loadingKey,
    );

    if (isMealInFlight) {
      return;
    }

    setLoadingSelections((prev) => new Set(prev).add(loadingKey));

    const revertSelection = () => {
      setMealSelections((prev) => {
        const next = { ...prev };
        if (isCurrentlySelected) {
          next[selectionKey] = recipeId;
        } else {
          delete next[selectionKey];
        }
        return next;
      });
    };

    setMealSelections((prev) => ({
      ...prev,
      [selectionKey]: isCurrentlySelected ? undefined : recipeId,
    }));

    if (!isCurrentlySelected) {
      setDraftData((prevData) =>
        updateDraftSelectionState({
          draftData: prevData,
          dayNumber,
          mealType,
          recipeId,
          shouldSelect: true,
        }),
      );
    }

    if (!draftId) {
      setLoadingSelections((prev) => {
        const next = new Set(prev);
        next.delete(loadingKey);
        return next;
      });
      return;
    }

    const dayMeals = data.suggestions?.find((d) => d.day === dayNumber)?.meals as
      | Record<string, { mealOrder?: number }>
      | undefined;
    const mealOrder = getMealOrderByMealKey(dayMeals, mealType);
    if (!mealOrder) {
      showWarningToast('Meal order missing', 'Unable to update selection because meal order is missing.');
      return;
    }
    try {
      const response = isCurrentlySelected
        ? await deselectDraftRecipe(draftId, dayNumber, mealOrder)
        : await selectDraftRecipe(draftId, dayNumber, mealOrder, recipeId);

      if (response.success) {
        setDraftData((prevData) =>
          updateDraftSelectionState({
            draftData: prevData,
            dayNumber,
            mealType,
            recipeId,
            shouldSelect: !isCurrentlySelected,
            dayWiseNutrition: response.data?.status?.dayWiseNutrition,
          }),
        );
      } else {
        revertSelection();
        showErrorToast(response.message || `Failed to ${isCurrentlySelected ? 'deselect' : 'select'} recipe. Please try again.`, 'Action Failed');
      }
    } catch (error) {
      revertSelection();
      showErrorToast(error, 'Action Failed');
    } finally {
      setLoadingSelections((prev) => {
        const newSet = new Set(prev);
        newSet.delete(loadingKey);
        return newSet;
      });
    }
  };

  // Validation functions
  const getMissingSelections = () => {
    const missing: Array<{ day: number; mealType: string }> = [];
    
    data.suggestions?.forEach(day => {
      Object.entries(day.meals || {}).forEach(([mealType, mealData]) => {
        // Check if any recipe in this meal is selected based on API response
        const hasSelectedRecipe = mealData.recipes?.some(recipe => recipe.isSelected);
        if (!hasSelectedRecipe) {
          missing.push({ day: day.day, mealType });
        }
      });
    });
    
    return missing;
  };

  const validateSelections = () => {
    const missing = getMissingSelections();
    return missing.length === 0;
  };

  const handleSavePlan = async () => {
    if (!validateSelections()) {
      setValidationModalOpen(true);
      return;
    }

    setSaving(true);
    try {
      // Finalize the draft
      if (draftId) {
        const userChosenName = (location.state as { planName?: string } | null)?.planName?.trim();
        const startDate = data.searchParams?.startDate || data.suggestions?.[0]?.date;
        const endDate = data.suggestions?.[data.suggestions.length - 1]?.date;
        const days = data.searchParams?.days || data.suggestions?.length || 0;
        const timestamp = format(new Date(), 'MMM d, yyyy HH:mm');
        const basePlanName = startDate && endDate
          ? `Meal Plan - ${format(new Date(startDate), 'MMM d')} to ${format(new Date(endDate), 'MMM d, yyyy')}`
          : `Meal Plan - ${days} Days`;
        const planName = userChosenName || `${basePlanName} (${timestamp})`;

        const finalizeResponse = await finalizeDraft(draftId, planName);
        
      
        if (finalizeResponse.success) {
          const inner = finalizeResponse.data?.data;
          const planId = inner?.mealPlanId ?? inner?.planId ?? inner?.id ?? finalizeResponse.data?.mealPlanId ?? finalizeResponse.data?.planId ?? finalizeResponse.data?.id ?? draftId;
          if (planId && clientId) {
            showSuccessToast('Meal plan finalized successfully.');
            const planResponse = await getMealPlanForDisplay(planId);
            const mealPlanData = planResponse.success && planResponse.data ? planResponse.data : undefined;
            setTimeout(() => {
              navigate(`/finalized-meal-plan/${planId}/client/${clientId}`, {
                state: {
                  from: locationState?.from || fallbackBackPath,
                  ...(mealPlanData && { mealPlanData }),
                },
              });
            }, 300);
          } else {
            showSuccessToast('Meal plan finalized successfully.');
          }
        } else {
        
          throw new Error(finalizeResponse.message || 'Failed to finalize draft');
        }
      }
    } catch (error) {
      showErrorToast(error, "Save Failed");
    } finally {
      setSaving(false);
    }
  };

  const handleValidationCancel = () => {
    setValidationModalOpen(false);
  };

  const planDuration = data?.suggestions?.length || data?.searchParams?.days || 0;
  const resolvedGoals = resolveClientGoals({
    clientDetails,
    planGoals: data?.searchParams?.clientGoals ?? null,
    overrideGoals: data?.searchParams?.overrideClientGoals ?? null,
    macroRangeFallback: 'whenClientGoalMissing',
  });
  const goalItems = buildGoalDisplayItems(resolvedGoals);
  const mealSlotTargetsSummary = buildMealSlotTargetsSummary(data?.searchParams);

  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
        }}
      >
        <div className="space-y-6 p-4 sm:p-5 lg:px-7 lg:pt-6 lg:pb-7">
      {!hideHeader && (
        <>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(backTarget)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Meal Planning
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Meal Plan</h1>
              <p className="text-gray-600">
                {(() => {
                  const totalSelections =
                    data.suggestions?.reduce(
                      (total, day) => total + Object.keys(day.meals || {}).length,
                      0
                    ) || 0;

                  const madeSelections =
                    data.suggestions?.reduce((total, day) => {
                      return (
                        total +
                        Object.values(day.meals || {}).filter(mealData =>
                          mealData.recipes?.some(recipe => recipe.isSelected)
                        ).length
                      );
                    }, 0) || 0;

                  return (
                    <span className="ml-2 text-sm">
                      • {madeSelections}/{totalSelections} meals selected
                    </span>
                  );
                })()}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <FeedbackButton
              clientId={clientId || draftData?.clientId || undefined}
              mealPlanId={draftId || undefined}
              label="Testing Feedback"
              defaultContext={planFeedbackContext}
              title="Individual Meal Plan Feedback"
              description="Rate the quality and relevance of this meal plan, add context, and describe any issues. Please specify the date of feedback. Multiple feedback entries allowed."
              titlePlaceholder="e.g., Meal plan quality issues"
              detailsPlaceholder="Describe the steps, date of feedback, and observations about this meal plan."
              submitLabel="Submit Meal Plan Feedback"
              successDescription="Thanks for helping improve meal planning."
            />
            <Button variant="outline" size="sm" onClick={handleSavePlan} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Plan'}
            </Button>
          </div>
        </div>

          {/* Client Profile Card */}
          {clientId && clientDetails && (
            <ClientDetailsBadge
              clientId={clientId}
              clientDetails={clientDetails}
              variant="card"
              planDuration={planDuration}
              status={draftData?.status || 'Draft'}
            />
          )}
        </>
      )}

      <ClientGoalsCard
        goalItems={goalItems}
        mealSlotTargetsSummary={mealSlotTargetsSummary?.text}
      />

      {/* Day Tabs */}
      <Tabs value={activeDayTab} onValueChange={setActiveDayTab} className="w-full">
        <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${data.suggestions?.length || 0}, 1fr)` }}>
          {data.suggestions.map((day) => {
            const displayName = day.dayName?.trim() || `Day ${day.day}`;
            return (
              <TabsTrigger key={day.day} value={`day-${day.day}`} className="flex items-center justify-center gap-1.5">
                <Calendar className="h-4 w-4 shrink-0" />
                <span className="truncate">{displayName}</span>
                <span className="ml-1 shrink-0 text-xs opacity-75">
                  {format(new Date(day.date), 'MMM dd')}
                </span>
                {draftId && (
                  <DayNameEditPopover
                    open={editingDayNameFor === day.day}
                    onOpenChange={(open) => {
                      if (!open) setEditingDayNameFor(null);
                      else setEditingDayNameFor(day.day);
                    }}
                    dayNumber={day.day}
                    value={day.dayName ?? ''}
                    saving={savingDayName}
                    hasExistingName={!!day.dayName}
                    onSave={async (valueToSave) => {
                      if (!draftId) return;
                      setSavingDayName(true);
                      try {
                        const res = await updateMealPlanDayName(draftId, { day: day.day, dayName: valueToSave || null });
                        if (res?.success) {
                          setDraftData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  suggestions: prev.suggestions.map((s) =>
                                    s.day === day.day ? { ...s, dayName: valueToSave || null } : s
                                  ),
                                }
                              : null
                          );
                          showSuccessToast(res?.message ?? 'Day name updated');
                          setEditingDayNameFor(null);
                        } else {
                          showErrorToast(res?.message ?? 'Failed to update day name');
                        }
                      } catch {
                        showErrorToast('Failed to update day name');
                      } finally {
                        setSavingDayName(false);
                      }
                    }}
                    onClear={async () => {
                      if (!draftId) return;
                      setSavingDayName(true);
                      try {
                        const res = await updateMealPlanDayName(draftId, { day: day.day, dayName: null });
                        if (res?.success) {
                          setDraftData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  suggestions: prev.suggestions.map((s) =>
                                    s.day === day.day ? { ...s, dayName: null } : s
                                  ),
                                }
                              : null
                          );
                          showSuccessToast(res?.message ?? 'Day name cleared');
                          setEditingDayNameFor(null);
                        } else {
                          showErrorToast(res?.message ?? 'Failed to clear day name');
                        }
                      } catch {
                        showErrorToast('Failed to clear day name');
                      } finally {
                        setSavingDayName(false);
                      }
                    }}
                  />
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {data.suggestions.map((day) => (
          <TabsContent key={day.day} value={`day-${day.day}`} className="mt-6">
            <div className="space-y-4">
              {/* Day Header Card */}
              <MealPlanDayHeader
                dayNumber={day.day}
                date={day.date}
                totalMeals={Object.keys(day.meals).length}
                selectedMeals={
                  Object.values(day.meals).filter((mealData) =>
                    mealData.recipes?.some((recipe) => recipe.isSelected)
                  ).length
                }
              />

              {/* Meal Sections */}
              <div className="space-y-4">
                {Object.entries(day.meals).map(([mealType, mealData]) => {
                  const mealKey = `${day.day}-${mealType}`;

                  return (
                    <MealPlanMealSection
                      key={mealType}
                      dayNumber={day.day}
                      mealType={mealType}
                      mealData={mealData}
                      isExpanded={expandedMeals.has(mealKey)}
                      loadingPagination={loadingPagination.has(mealKey)}
                      loadingSelections={loadingSelections}
                      onToggle={toggleMealExpansion}
                      onSelectRecipe={handleMealSelection}
                      onViewRecipe={handleViewDetails}
                      onPageChange={handlePageChange}
                    />
                  );
                })}
              </div>

              {/* Nutrition Analysis Section */}
              {(() => {
                // Get day-specific nutrition data
                const dayKey = `day${day.day}`;
                const dayWiseData = draftData?.dayWiseNutrition?.[dayKey];
                const nutritionData = dayWiseData?.nutrition;
                
                const dayNutrition = {
                  calories: nutritionData?.totalCalories || 0,
                  protein: nutritionData?.totalProtein || 0,
                  carbs: nutritionData?.totalCarbs || 0,
                  fat: nutritionData?.totalFat || 0,
                  fiber: nutritionData?.totalFiber || 0,
                  sodium: nutritionData?.totalSodium || 0,
                  sugar: nutritionData?.totalSugar || 0,
                  cholesterol: nutritionData?.totalCholesterol || 0,
                  saturatedFat: nutritionData?.macros?.saturatedFat?.quantity || 0,
                  calcium: nutritionData?.micros?.minerals?.calcium?.quantity || nutritionData?.totalCalcium || 0,
                  vitaminB12: nutritionData?.micros?.vitamins?.vitaminB12?.quantity || 0,
                  phosphorus: nutritionData?.micros?.minerals?.phosphorus?.quantity || 0,
                };

                // Get client goals from draftData
                const clientGoals = {
                  eerGoalCalories: Number(draftData?.searchParams?.clientGoals?.calories || 0),
                  proteinGoalMin: Number(draftData?.searchParams?.overrideClientGoals?.proteinGoalMin || draftData?.searchParams?.clientGoals?.protein || 0),
                  proteinGoalMax: Number(draftData?.searchParams?.overrideClientGoals?.proteinGoalMax || draftData?.searchParams?.clientGoals?.protein || 0),
                  carbsGoalMin: Number(draftData?.searchParams?.overrideClientGoals?.carbsGoalMin || draftData?.searchParams?.clientGoals?.carbs || 0),
                  carbsGoalMax: Number(draftData?.searchParams?.overrideClientGoals?.carbsGoalMax || draftData?.searchParams?.clientGoals?.carbs || 0),
                  fatGoalMin: Number(draftData?.searchParams?.overrideClientGoals?.fatGoalMin || draftData?.searchParams?.clientGoals?.fat || 0),
                  fatGoalMax: Number(draftData?.searchParams?.overrideClientGoals?.fatGoalMax || draftData?.searchParams?.clientGoals?.fat || 0),
                  fiberGoalGrams: Number(draftData?.searchParams?.clientGoals?.fiber || 0),
                };

                return (
                  <div className="mt-6">
                    <DayNutritionAnalysis
                      dayNumber={day.day}
                      date={day.date}
                      dayNutrition={dayNutrition}
                      clientGoals={clientGoals}
                      clientDetails={clientDetails}
                    />
                  </div>
                );
              })()}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Recipe Details Modal */}
      {selectedRecipeId && (
        <RecipeDetailsModal
          recipeId={selectedRecipeId}
          recipeName={selectedRecipeName}
          open={modalOpen}
          onOpenChange={setModalOpen}
          draftId={draftId || undefined}
          day={selectedRecipeContext?.day}
          mealName={selectedRecipeContext?.mealName}
          mealOrder={selectedRecipeContext?.mealOrder}
          source={selectedRecipeContext?.source as 'spoonacular' | 'edamam'}
          onClose={handleModalClose}
          onRefetchDraft={refetchDraftInBackground}
        />
      )}

      {/* Meal Selection Validation Modal */}
      <MealSelectionValidationModal
        open={validationModalOpen}
        onOpenChange={setValidationModalOpen}
        missingSelections={getMissingSelections()}
        onCancel={handleValidationCancel}
      />
    </div>
      </Paper>
    </Box>
  );
};

const AutomatedMealDraftPage = () => {
  return <AutomatedMealDraftContent />;
};

export default AutomatedMealDraftPage;
